import { getEmptyOffsetPagedResults, OffsetPagedResults } from '~interfaces';
import DeviceLog from '~interfaces/deviceLog';
import { SkcOffsetPagedResponse } from '~interfaces/responses';
import SkopeiConnectService from '~services/skopeiConnectService';

import Log from '../interfaces/log';

class LogsService extends SkopeiConnectService {
	protected readonly path = 'logs';

	async getLogs(
		deviceId: string,
		page = 1,
		pageSize = 10,
		signal?: AbortSignal
	): Promise<OffsetPagedResults<Log>> {
		const endpoint = this.createEndpoint('v2', ['access']);
		const { data } = await this.client.get<SkcOffsetPagedResponse<LogResponse>>(endpoint, {
			signal: signal,
			params: {
				page: page,
				pageSize: pageSize,
				deviceId: deviceId,
			},
		});

		return this.mapSkcOffsetPagedResponse(data, LogsService.fromResponse);
	}

	public getLogsCacheKey(deviceId: string) {
		return [this.path, deviceId, 'access'] as const;
	}

	/**
	 * Get a list of metrics.
	 *
	 * @param page The page.
	 * @param pageSize The page size.
	 * @param deviceId The id of the device to filter on.
	 * @param startDate The start date to filter on.
	 * @param endDate The end date to filter on.
	 * @param lockState The lock state to filter on.
	 * @param overwrittenClientId The client id if getting it for another organisation as super admin.
	 *
	 * @returns The list of metrics.
	 */
	async getDeviceLogs(params: {
		page: string;
		pageSize: string;
		deviceId: string;
		startDate: string;
		endDate: string;
		deviceState?: string;
		lockState?: string;
		overwrittenClientId?: string;
	}): Promise<OffsetPagedResults<DeviceLog>> {
		const endpoint = this.createEndpoint('v2', ['device-logs', params.deviceId, 'paged']);
		return await this.client
			.get<SkcOffsetPagedResponse<DeviceLogResponse>>(endpoint, {
				signal: this._controller.signal,
				params: {
					'page-number': params.page,
					'page-size': params.pageSize,
					start: params.startDate,
					end: params.endDate,
					'device-state': params.deviceState,
					'lock-state': params.lockState,
				},
				headers: {
					OverwrittenClientId: params.overwrittenClientId,
				},
			})
			.then((response) => {
				if (!response.data) {
					return Promise.resolve(
						getEmptyOffsetPagedResults<DeviceLog>(Number(params.page), Number(params.pageSize))
					);
				}

				return Promise.resolve(
					this.mapSkcOffsetPagedResponse(response.data, this.fromDeviceLogResponse)
				);
			});
	}

	public getDeviceLogsCacheKey(deviceId: string, searchParams: URLSearchParams) {
		return [this.path, deviceId, searchParams, 'device-logs', 'paged'] as const;
	}

	static fromResponse = (data: LogResponse): Log => {
		const { serverTimestamp, ...rest } = data;

		return {
			...rest,
			serverTimestamp: new Date(serverTimestamp),
		};
	};

	private fromDeviceLogResponse(data: DeviceLogResponse): DeviceLog {
		return {
			avlUndefined: data.avlUndefined,
			canBus1: data['can.bus1'],
			canBus2: data['can.bus2'],
			canBus3: data['can.bus3'],
			fuelLevelPercentage: data['fuelLevelPercentage'],
			canProgram: data['can.program'],
			canSecurityStateFlags: data['can.securityStateFlags'],
			batteryRangeMeters: data.batteryRangeMeters,
			speedKiloMeters: data['can.speedKiloMeters'],
			clientId: data.clientId,
			codecId: data.codecId,
			deviceId: data.deviceId,
			imei: data.imei,
			locationAltitude: data['location.altitude'],
			locationAngle: data['location.angle'],
			locationLatitude: data['location.latitude'],
			locationLongitude: data['location.longitude'],
			locationSatellitesUsed: data['location.satellites.used'],
			locationSpeed: data['location.speed'],
			lockState: data.lockState,
			deviceTimestamp: data.deviceTimestamp,
			eventCode: data.eventCode,
			id: data.id,
			ioBatteryPercentage: data['io.batteryPercentage'],
			ioBluetoothStatus: data['io.bluetoothStatus'],
			ioDigitalIn1: data['io.digitalIn1'],
			ioDigitalIn2: data['io.digitalIn2'],
			ioDigitalOut1: data['io.digitalOut1'],
			ioExternalVoltage: data['io.externalVoltage'],
			ioFuelConsumerLiters: data['io.fuelConsumerLiters'],
			locationGnssHdop: data['io.gnssHdop'],
			locationGnssPdop: data['io.gnssPdop'],
			locationGnssStatus: data['location.gnssStatus'],
			locationGmsActiveOperator: data['location.gmsActiveOperator'],
			locationGsmSignalAreaCode: data['location.gsmSignalAreaCode'],
			locationGsmSignalStrength: data['location.gsmSignalStrength'],
			ioIbuttonCode: data['io.ibuttonCode'],
			ioIccid: data['io.iccid'],
			isIgnitionOn: data.isIgnitionOn,
			isMoving: data.isMoving,
			ioPropertyCount: data['io.propertyCount'],
			ioRfidTag: data['io.rfidTag'],
			ioSleepMode: data['io.sleepMode'],
			ioSpeedKiloMetersPerHour: data['io.speedKiloMetersPerHour'],
			ioMileageCountedInMeters: data['io.mileageCountedInMeters'],
			ioMileageInMeters: data['io.mileageInMeters'],
			ioOdoMeterTotal: data['io.odoMeterTotal'],
			ioTripOdoMeter: data['io.tripOdoMeter'],
			ioTripStatus: data['io.tripStatus'],
			ioUmtsLteId: data['io.umtsLteId'],
			priority: data.priority,
			serverTimestamp: data.serverTimestamp,
			batteryPercentage: data.batteryPercentage,
			chargerState: data.chargerState,
			configConnectivityProfile: data['config.connectivityProfile'],
			configNfcSensitivity: data['config.nfcSensitivity'],
			configNfcTuning: data['config.nfcTuning'],
			debug: data.debug,
			firmwareVersion: data['firmware.version'],
			hardwareId: data.hardwareId,
			locationResult: data['location.result'],
			locationSatellitesSeen: data['location.satellites.seen'],
			locationTtf: data['location.ttf'],
			lockAssetFirmwareVersion: data['lockAsset.firmwareVersion'],
			lockAssetId: data['lockAsset.id'],
			modemCellId: data['modem.cell.id'],
			modemCellLac: data['modem.cell.lac'],
			modemCellMcc: data['modem.cell.mcc'],
			modemCellMnc: data['modem.cell.mnc'],
			modemCellRsrp: data['modem.cell.rsrp'],
			modemCoprocessorFirmwareVersion: data['modem.coprocessor.firmware.version'],
			modemFirmwareVersion: data['modem.firmware.version'],
			modemImei: data['modem.imei'],
			modemSimIccid: data['modem.sim.iccid'],
			modemSimImsi: data['modem.sim.imsi'],
			configOfflineKeyCount: data['config.offlineKeyCount'],
			rssi: data.rssi,
			deviceState: data.deviceState,
			statsTotalReboots: data['stats.totalReboots'],
			statsTotalUnlocks: data['stats.totalUnlocks'],
			statusCode: data.statusCode,
			temperature: data.temperature,
			wheelTurns: data.wheelTurns,
			eventType: data.eventType,
			keySerialNumber: data['key.serialNumber'],
			keyTagType: data['key.tagType'],
			keyTokenId: data['key.tokenId'],
			proxyId: data['proxy.id'],
			proxyTimeZone: data['proxy.timeZone'],
			proxyType: data['proxy.type'],
			timeStamp: data.timeStamp,
			traceId: data.traceId,
			accessCorrelationId: data['access.correlationId'],
			accessKeyId: data['access.keyId'],
			accessRuleId: data['access.ruleId'],
			accessState: data['access.state'],
			accessType: data['access.type'],
			statsTotalAccessCalls: data['stats.totalAccessCalls'],
			statsTotalAccessError: data['stats.totalAccessError'],
			statsTotalAccessFailed: data['stats.totalAccessFailed'],
			statsTotalAccessSuccess: data['stats.totalAccessSuccess'],
			statsTotalReconnects: data['stats.totalReconnects'],
			borderRouterHostName: data['borderRouter.hostName'],
			borderRouterIfMode: data['borderRouter.ifMode'],
			borderRouterIpAddress: data['borderRouter.ipAddress'],
			borderRouterThreadMode: data['borderRouter.threadMode'],
			borderRouterTxPower: data['borderRouter.txPower'],
		};
	}
}

interface DeviceLogResponse {
	'access.correlationId': string;
	'access.keyId': number;
	'access.ruleId': number;
	'access.state': string;
	'access.type': string;
	clientId: number;
	batteryPercentage: number;
	'config.nfcSensitivity': number;
	'config.nfcTuning': string;
	debug: string;
	'firmware.version': string;
	hardwareId: string;
	deviceId: number;
	'location.result': string;
	'location.satellites.seen': number;
	'location.satellites.used': number;
	'location.latitude': number;
	'location.longitude': number;
	'location.ttf': number;
	'lockAsset.firmwareVersion': string;
	'lockAsset.id': string;
	lockState: string;
	'config.offlineKeyCount': number;
	rssi: number;
	deviceState: string;
	'stats.totalAccessCalls': number;
	'stats.totalAccessError': number;
	'stats.totalAccessFailed': number;
	'stats.totalAccessSuccess': number;
	'stats.totalReboots': number;
	'stats.totalReconnects': number;
	'stats.totalUnlocks': number;
	statusCode: number;
	temperature: number;
	eventCode: string;
	eventType: string | undefined;
	id: string;
	'key.serialNumber': string | undefined;
	'key.tagType': string;
	'key.tokenId': string;
	'proxy.id': string;
	'proxy.timeZone': string;
	'proxy.type': string;
	timeStamp: string;
	traceId: string;
	chargerState: 'chargerNotConnected';
	'config.connectivityProfile': number;
	'modem.cell.id': number;
	'modem.cell.lac': number;
	'modem.cell.mcc': number;
	'modem.cell.mnc': number;
	'modem.cell.rsrp': number;
	'modem.coprocessor.firmware.version': string;
	'modem.firmware.version': string;
	'modem.imei': string;
	'modem.sim.iccid': string;
	'modem.sim.imsi': string;
	avlUndefined: string;
	'can.bus1': string;
	'can.bus2': string;
	'can.bus3': string;
	fuelLevelPercentage: number;
	'can.program': number;
	'can.securityStateFlags': string;
	batteryRangeMeters: number;
	'can.speedKiloMeters': number;
	codecId: string;
	imei: string;
	'location.altitude': number;
	'location.angle': number;
	'location.satellites': number;
	'location.speed': number;
	'location.gnss.hdop': number;
	'location.gnss.pdop': number;
	'location.gnssStatus': string;
	'location.gmsActiveOperator': number;
	'location.gsmSignalAreaCode': number;
	'location.gsmSignalStrength': string;
	deviceTimestamp: string;
	'io.batteryPercentage': number;
	'io.bluetoothStatus': string;
	'io.digitalIn1': boolean;
	'io.digitalIn2': boolean;
	'io.digitalOut1': boolean;
	'io.externalVoltage': number;
	'io.fuelConsumerLiters': number;
	'io.gnssHdop': number;
	'io.gnssPdop': number;
	'io.gnssStatus': string;
	'io.gmsActiveOperator': number;
	'io.gsmSignalAreaCode': number;
	'io.gsmSignalStrength': string;
	'io.ibuttonCode': string;
	'io.iccid': string;
	isIgnitionOn: boolean;
	'io.isMoving': boolean;
	'io.propertyCount': number;
	'io.rfidTag': number;
	'io.sleepMode': string;
	'io.speedKiloMetersPerHour': number;
	'io.mileageCountedInMeters': number;
	'io.mileageInMeters': number;
	'io.odoMeterTotal': number;
	'io.tripOdoMeter': number;
	'io.tripStatus': string;
	'io.umtsLteId': number;
	priority: string;
	serverTimestamp: string;
	isMoving: boolean;
	wheelTurns: number;
	'borderRouter.hostName': string;
	'borderRouter.ifMode': number;
	'borderRouter.ipAddress': string;
	'borderRouter.threadMode': number;
	'borderRouter.txPower': number;
}

interface LogResponse {
	id: string;
	serverTimestamp: Date;
	action: string;
	modifiedBy: string; // Probably want some user model, but we don't have that yet.
}

export default LogsService;
